import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Text,
  Badge,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular,
  Edit20Regular,
  Copy20Regular,
  Delete20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
} from "@fluentui/react-icons";
import SignaturePreview from "../components/SignaturePreview";

const useStyles = makeStyles({
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  intro: { color: tokens.colorNeutralForeground3, maxWidth: "640px" },
  list: { display: "flex", flexDirection: "column", gap: "12px" },
  card: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "14px 16px",
    background: "#fff",
    borderRadius: "6px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow2,
  },
  thumb: { flexShrink: 0 },
  body: { flexGrow: 1, minWidth: 0 },
  name: { display: "flex", alignItems: "center", gap: "8px" },
  conds: { color: tokens.colorNeutralForeground3, marginTop: "4px" },
  actions: { display: "flex", gap: "2px", flexShrink: 0 },
  empty: { padding: "40px", textAlign: "center", color: tokens.colorNeutralForeground3 },
});

function conditionSummary(rule) {
  const c = rule.conditions || {};
  const parts = [];
  if (c.audience) parts.push(`${c.audience} recipients`);
  if (c.itemType) parts.push(c.itemType);
  if (c.department) parts.push(`dept: ${[].concat(c.department).join(", ")}`);
  if (c.group) parts.push(`group: ${[].concat(c.group).join(", ")}`);
  if (c.jobTitleContains) parts.push(`title ~ "${c.jobTitleContains}"`);
  if (c.userEmail) parts.push(`users: ${[].concat(c.userEmail).join(", ")}`);
  if (parts.length === 0) parts.push("all senders & recipients");
  return parts.join("  •  ");
}

export default function RulesList({ assignments, templates, onChange, onEdit }) {
  const styles = useStyles();
  const rules = assignments.rules || [];

  const setRules = (next) => onChange({ ...assignments, rules: next });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[i], next[j]] = [next[j], next[i]];
    setRules(next);
  };
  const clone = (i) => {
    const copy = { ...rules[i], name: `${rules[i].name} (copy)` };
    const next = [...rules];
    next.splice(i + 1, 0, copy);
    setRules(next);
  };
  const remove = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const templateHtml = (id) => {
    const t = (templates.templates || []).find((x) => x.id === id);
    return t ? t.html : "<em>template missing</em>";
  };
  const templateName = (id) => {
    const t = (templates.templates || []).find((x) => x.id === id);
    return t ? t.name : id;
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <Text className={styles.intro}>
          Rules are evaluated top to bottom — the first match wins. Drag order with the arrows.
          A rule applies the chosen signature to senders/recipients that match its conditions.
        </Text>
        <Button appearance="primary" icon={<Add24Regular />} onClick={() => onEdit(-1)}>
          Create rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className={styles.empty}>No rules yet. Click “Create rule” to add your first signature rule.</div>
      ) : (
        <div className={styles.list}>
          {rules.map((rule, i) => {
            const isDefault = templates.defaultTemplateId === rule.templateId;
            const disabled = rule.enabled === false;
            const scheduled = rule.schedule && (rule.schedule.start || rule.schedule.end);
            return (
              <div className={styles.card} key={i}>
                <div className={styles.thumb}>
                  <SignaturePreview html={templateHtml(rule.templateId)} scale={0.55} width={220} height={96} />
                </div>
                <div className={styles.body}>
                  <div className={styles.name}>
                    <Text weight="semibold" size={400}>{rule.name || "Untitled rule"}</Text>
                    {disabled ? (
                      <Badge appearance="tint" color="informative">Disabled</Badge>
                    ) : (
                      <Badge appearance="filled" color="success">Active</Badge>
                    )}
                    {scheduled && <Badge appearance="tint" color="warning">Scheduled</Badge>}
                    {isDefault && <Badge appearance="tint" color="brand">Default</Badge>}
                  </div>
                  <Text size={200} className={styles.conds}>
                    {conditionSummary(rule)} → <b>{templateName(rule.templateId)}</b>
                  </Text>
                </div>
                <div className={styles.actions}>
                  <Tooltip content="Move up" relationship="label">
                    <Button appearance="subtle" icon={<ArrowUp20Regular />} onClick={() => move(i, -1)} disabled={i === 0} />
                  </Tooltip>
                  <Tooltip content="Move down" relationship="label">
                    <Button appearance="subtle" icon={<ArrowDown20Regular />} onClick={() => move(i, 1)} disabled={i === rules.length - 1} />
                  </Tooltip>
                  <Tooltip content="Clone" relationship="label">
                    <Button appearance="subtle" icon={<Copy20Regular />} onClick={() => clone(i)} />
                  </Tooltip>
                  <Tooltip content="Edit" relationship="label">
                    <Button appearance="subtle" icon={<Edit20Regular />} onClick={() => onEdit(i)} />
                  </Tooltip>
                  <Tooltip content="Delete" relationship="label">
                    <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => remove(i)} />
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
